import { cn, Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';

import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { KycEnum } from '@/api/rest/resources/types/user';
import { MailLink } from '@/components/widgets/a/Link/Mail';
import { CONTACT_SUPPORT_EMAIL_ADDRESS } from '@/config/constants';
import { useAccountPageRoutes } from '@/hooks/useAccountPageRoutes';
import { useIsConservation } from '@/hooks/useIsConservation';
import { useOrganization } from '@/hooks/useOrganization';
import { useUserMe } from '@/hooks/useUserMe';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { printFullName } from '@/utils/formatting/user';

import {
  ActionButton,
  EmailInformationBoxEntryStatus,
  EmailVerificationStatus,
  InformationBox,
  InformationBoxEntry,
  SelectCurrency,
  SelectLanguage,
} from '../../../components';
import { AccountKycStatus } from '../../../components/kyc/status/status';
import { AccountKycVerificationBox } from '../../../components/kyc/verification-box/VerificationBox';

export const DesktopView = () => {
  const { t } = useTranslation();

  const user = useUserMe().data;
  const { accountEditEmailPath, accountEditPasswordPath, accountEditNamePath } = useAccountPageRoutes();

  const isConservation = useIsConservation();

  const organizationId = useOrganizationId();
  const organization = useOrganization({ id: organizationId }).data.results[0];
  const companyName = organization?.name;

  const isUserLandSteward = user.membership.type === MembershipWithOrganizationTypeEnum.land_steward;
  const isKycSectionShown = isUserLandSteward && user.kyc !== KycEnum.success;
  const editButtonLabel = t('shared.account.buttons.edit');
  return (
    <Stack
      spacing={3}
      className={cn('mx-auto rounded', {
        'border border-neutral-black-12': !isConservation,
      })}
      data-testid='account-page-desktop-view'
    >
      <InformationBox
        title={t('shared.account.titles.loginData')}
        entries={[
          <InformationBoxEntry
            key='email'
            label={t('shared.account.labels.email')}
            value={user.email}
            status={
              <EmailInformationBoxEntryStatus
                status={user.email_activated ? EmailVerificationStatus.VERIFIED : EmailVerificationStatus.UNVERIFIED}
              />
            }
            ActionButton={<ActionButton label={editButtonLabel} link={accountEditEmailPath} />}
          />,
          <InformationBoxEntry
            key='password'
            label={t('shared.account.labels.password')}
            value='******'
            ActionButton={<ActionButton label={editButtonLabel} link={accountEditPasswordPath} />}
          />,
        ]}
      />
      <InformationBox
        title={t('shared.account.titles.personalData')}
        entries={[
          <InformationBoxEntry
            key='name'
            label={t('shared.account.labels.name')}
            value={printFullName(user)}
            ActionButton={
              <ActionButton
                label={editButtonLabel}
                link={accountEditNamePath}
                disabled={[KycEnum.in_progress, KycEnum.success].includes(user.kyc)}
                disabledHint={
                  user.kyc === KycEnum.in_progress
                    ? t('shared.account.popovers.editNameDisabledDueToKycInProgress')
                    : t('shared.account.popovers.editNameDisabledDueToKycSuccess')
                }
              />
            }
            status={isUserLandSteward ? <AccountKycStatus status={user.kyc} /> : undefined}
          />,
          <InformationBoxEntry key='company' label={t('shared.account.labels.company')} value={companyName ?? ''} />,
        ]}
      />
      {isKycSectionShown && <AccountKycVerificationBox status={user.kyc} />}
      <InformationBox
        title={t('shared.account.titles.settings')}
        entries={[<SelectLanguage key='select-language' />, <SelectCurrency key='select-currency' />]}
      />
      <InformationBox
        title={t('shared.account.titles.customerSupport')}
        entries={[
          <span key='customer-support' className='typography-body1'>
            <Trans
              i18nKey='shared.account.labels.customerSupportDescription'
              values={{ email: CONTACT_SUPPORT_EMAIL_ADDRESS }}
              components={{
                a: <MailLink email={CONTACT_SUPPORT_EMAIL_ADDRESS} />,
              }}
            />
          </span>,
        ]}
      />
    </Stack>
  );
};
